import { useEffect, useMemo, useState } from "react";
import DtoolsClarityTabPanel from "@/components/sales/dtools/DtoolsClarityTabPanel";
import DtoolsProposalPdfPackage from "@/components/sales/dtools/DtoolsProposalPdfPackage";
import DtoolsProposalDisclaimersFooter from "@/components/sales/dtools/DtoolsProposalDisclaimersFooter";
import DtoolsReviewAccordionSection from "@/components/sales/dtools/DtoolsReviewAccordionSection";
import DtoolsReviewSystemSection from "@/components/sales/dtools/DtoolsReviewSystemSection";
import DtoolsV1SystemSummaryTab from "@/components/sales/dtools/DtoolsV1SystemSummaryTab";
import {
  DtoolsV2AlternativeGroupView,
  DtoolsV2IndependentSectionView,
} from "@/components/sales/dtools/DtoolsV2ProposalTabPanel";
import DtoolsV2SystemSummaryTab from "@/components/sales/dtools/DtoolsV2SystemSummaryTab";
import ProposalRetailTermsCollapsible from "@/components/sales/ProposalRetailTermsCollapsible";
import ProposalSectionTabNav from "@/components/proposal/ProposalSectionTabNav";
import { PROPOSAL_COPY_FONT } from "@/lib/proposalTheme";
import type { DtoolsDisplaySettings, DtoolsPortalPaymentTerm } from "@shared/dtoolsDisplaySettings";
import {
  buildDtoolsCustomerTabItems,
  type DtoolsCustomerProposalTabId,
} from "@shared/dtoolsCustomerProposalView";
import {
  buildV2CustomerProposalView,
  type DtoolsV2CustomerProposalTabId,
  type DtoolsV2CustomerProposalView,
} from "@shared/dtoolsV2CustomerProposalView";
import { resolveDtoolsCustomerTabGate } from "@shared/dtoolsSystemClassification";
import {
  DTOOLS_CUSTOMER_PREVIEW_VISIBILITY_AUDIENCE,
  applyCustomerFacingRoomVisibility,
  filterVisibleDtoolsSystems,
  isCustomerFacingDtoolsSystem,
  isDtoolsOptionHidden,
} from "@shared/dtoolsOptionVisibility";
import { enrichDtoolsReviewBuildContext, buildDtoolsReviewProposalTotals, type DtoolsReviewBuildContext } from "@shared/dtoolsReviewNavigation";
import { proposalShowsRetailPriceDisclaimer } from "@shared/proposalRetailPriceDisclaimer";
import type { DtoolsProposalPresentation } from "@shared/dtoolsProposalPresentation";
import {
  resolveProposalCustomerPricingDisplay,
  type ProposalCustomerPricingDisplay,
} from "@shared/proposalCustomerPricing";
import type { DtoolsSystemClassificationsMap, DtoolsTabModelVersion } from "@shared/dtoolsSystemClassification";
import type { DtoolsReviewFooter, DtoolsReviewSystem } from "@shared/dtoolsStagingTransform";
import type { DtoolsOptionSectionActionsProps } from "@/components/sales/dtools/DtoolsOptionSectionActions";
import type { DtoolsPdfPackageMeta } from "@shared/dtoolsPdfPackage";
import { ProposalCustomerPricingPanelFromDisplay } from "@/components/proposal/ProposalCustomerPricingPanel";

type Props = {
  systems: DtoolsReviewSystem[];
  footer: DtoolsReviewFooter;
  reviewContext?: DtoolsReviewBuildContext;
  presentation?: DtoolsProposalPresentation;
  paymentTerms?: DtoolsPortalPaymentTerm[];
  createdAt?: string | null;
  linkBExpiresAt?: string | Date | null;
  approvedAt?: string | Date | null;
  status?: string | null;
  pipelineStage?: string | null;
  audience?: "customer" | "staff";
  optionActions?: Omit<DtoolsOptionSectionActionsProps, "optionLabel">;
  displaySettings?: DtoolsDisplaySettings;
  tabModelVersion?: DtoolsTabModelVersion;
  systemClassifications?: DtoolsSystemClassificationsMap;
  parsedMetadataJson?: unknown;
  customerPricing?: ProposalCustomerPricingDisplay | null;
  retailPricingJson?: {
    listTotal?: number | null;
    originalListPrice?: number | null;
    agreedPriceSgd?: number | null;
    agreedPrice?: number | null;
  } | null;
  totalAmount?: number | string | null;
  cover?: {
    customerName: string;
    proposalTitle: string;
    proposalRef: string;
  } | null;
  pdfPackageMeta?: DtoolsPdfPackageMeta;
  /**
   * Step 3 / Revise: exclusive Web vs PDF accordion (persists last choice).
   * Live Link A/B: leave false so PDF stays CSS-hidden for print/export.
   */
  dualPreviewAccordion?: boolean;
  optionSectionIds?: string[];
  navigationSystemKey?: string | null;
  navigationRequestToken?: number;
};

type ActiveTabId = DtoolsCustomerProposalTabId | DtoolsV2CustomerProposalTabId;

const DTOOLS_PREVIEW_SURFACE_STORAGE_KEY = "clarity:dtools-proposal-preview-surface";
type PreviewSurface = "web" | "pdf";

function readPreviewSurfacePreference(): PreviewSurface {
  try {
    const v = localStorage.getItem(DTOOLS_PREVIEW_SURFACE_STORAGE_KEY);
    if (v === "pdf" || v === "web") return v;
  } catch {
    /* ignore */
  }
  return "web";
}

function writePreviewSurfacePreference(surface: PreviewSurface) {
  try {
    localStorage.setItem(DTOOLS_PREVIEW_SURFACE_STORAGE_KEY, surface);
  } catch {
    /* ignore */
  }
}

function countV2CustomerApprovalOptions(
  view: DtoolsV2CustomerProposalView,
  displaySettings?: DtoolsDisplaySettings,
): number {
  let count = 0;
  for (const section of view.independentSections) {
    if (isCustomerFacingDtoolsSystem(section.system, displaySettings, DTOOLS_CUSTOMER_PREVIEW_VISIBILITY_AUDIENCE))
      count += 1;
  }
  for (const group of view.alternativeGroups) {
    for (const opt of group.options) {
      if (isCustomerFacingDtoolsSystem(opt.system, displaySettings, DTOOLS_CUSTOMER_PREVIEW_VISIBILITY_AUDIENCE))
        count += 1;
    }
  }
  return count;
}

export default function DtoolsCustomerProposalTabbedPreview({
  systems,
  footer,
  reviewContext,
  presentation,
  paymentTerms = [],
  createdAt,
  linkBExpiresAt,
  approvedAt,
  status,
  pipelineStage,
  audience: _audience = "customer",
  optionActions,
  displaySettings = {},
  tabModelVersion,
  systemClassifications,
  parsedMetadataJson,
  customerPricing: customerPricingProp,
  retailPricingJson,
  totalAmount,
  cover,
  pdfPackageMeta,
  dualPreviewAccordion = false,
  optionSectionIds,
  navigationSystemKey,
  navigationRequestToken = 0,
}: Props) {
  /** Preview surfaces always use customer visibility — staff audience is for PDF label / Cleo hints only. */
  const previewVisibilityAudience = DTOOLS_CUSTOMER_PREVIEW_VISIBILITY_AUDIENCE;
  const [previewSurface, setPreviewSurface] = useState<PreviewSurface>(() =>
    dualPreviewAccordion ? readPreviewSurfacePreference() : "web",
  );

  useEffect(() => {
    if (!dualPreviewAccordion) return;
    writePreviewSurfacePreference(previewSurface);
  }, [dualPreviewAccordion, previewSurface]);

  const tabGate = useMemo(
    () =>
      resolveDtoolsCustomerTabGate({
        systems,
        tabModelVersion,
        parsedMetadataJson,
        systemClassifications,
      }),
    [systems, tabModelVersion, parsedMetadataJson, systemClassifications],
  );

  const enrichedContext = useMemo(
    () =>
      enrichDtoolsReviewBuildContext(systems, {
        ...reviewContext,
        presentation: reviewContext?.presentation ?? presentation,
        systemClassifications: tabGate.systemClassifications,
      }),
    [systems, reviewContext, presentation, tabGate.systemClassifications],
  );

  const isV2 = tabGate.useV2TabModel;
  const effectiveClassifications = tabGate.systemClassifications;

  const customerPricing = useMemo(() => {
    if (customerPricingProp) return customerPricingProp;
    const overrideTotal =
      totalAmount != null && Number.isFinite(Number(totalAmount)) ? Number(totalAmount) : null;
    const visibleSystems = filterVisibleDtoolsSystems(systems, displaySettings, {
      audience: previewVisibilityAudience,
    }).map((entry) => entry.system);
    const totals = buildDtoolsReviewProposalTotals({
      systems: visibleSystems,
      footerAdjustments: footer.adjustments,
    });
    const finalTotal = overrideTotal ?? totals.finalTotal;
    return resolveProposalCustomerPricingDisplay({
      totalAmount: finalTotal,
      retailPricingJson: retailPricingJson ?? undefined,
      computedProposalTotal: finalTotal,
    });
  }, [
    customerPricingProp,
    systems,
    footer.adjustments,
    totalAmount,
    retailPricingJson,
    displaySettings,
    previewVisibilityAudience,
  ]);
  const hideCombinedProposalPricing = displaySettings.alternativeOptions === true;

  const v2View = useMemo(
    () =>
      isV2
        ? buildV2CustomerProposalView({
            systems,
            classifications: effectiveClassifications,
            displaySettings,
            reviewContext: enrichedContext,
            audience: previewVisibilityAudience,
          })
        : null,
    [isV2, systems, effectiveClassifications, displaySettings, enrichedContext, previewVisibilityAudience],
  );

  const enrichedOptionActions = useMemo(() => {
    if (!optionActions) return undefined;
    const actions = { ...optionActions, hideCombinedProposalPricing };
    if (v2View && countV2CustomerApprovalOptions(v2View, displaySettings) >= 2) {
      return { ...actions, multiOptionApproval: true as const };
    }
    return actions;
  }, [optionActions, v2View, displaySettings, hideCombinedProposalPricing]);

  const tabs = useMemo(() => {
    if (isV2 && v2View) return v2View.tabs;
    return buildDtoolsCustomerTabItems(systems, enrichedContext, displaySettings);
  }, [isV2, v2View, systems, enrichedContext, displaySettings]);

  const [activeTab, setActiveTab] = useState<ActiveTabId>(tabs[0]?.id ?? "terms");

  useEffect(() => {
    if (!tabs.some((tab) => tab.id === activeTab)) {
      setActiveTab(tabs[0]?.id ?? "terms");
    }
  }, [activeTab, tabs]);

  const navigationTargetIndex = useMemo(() => {
    const target = String(navigationSystemKey ?? "").trim().toLowerCase();
    if (!target) return -1;
    return systems.findIndex((system) =>
      [system.systemId, system.name].some(
        (value) => String(value ?? "").trim().toLowerCase() === target,
      ),
    );
  }, [navigationSystemKey, systems]);

  useEffect(() => {
    if (navigationTargetIndex < 0) return;
    if (dualPreviewAccordion) setPreviewSurface("web");
    if (!isV2) {
      setActiveTab(`option-${navigationTargetIndex}` as ActiveTabId);
      return;
    }
    const targetSystem = systems[navigationTargetIndex];
    if (!targetSystem || !v2View) return;
    const independent = v2View.independentSections.find(
      (section) =>
        String(section.system.systemId ?? section.system.name).trim().toLowerCase() ===
        String(targetSystem.systemId ?? targetSystem.name).trim().toLowerCase(),
    );
    if (independent) {
      setActiveTab(`independent-${independent.sectionKey}` as ActiveTabId);
      return;
    }
    const group = v2View.alternativeGroups.find((entry) =>
      entry.options.some(
        (option) =>
          String(option.system.systemId ?? option.system.name).trim().toLowerCase() ===
          String(targetSystem.systemId ?? targetSystem.name).trim().toLowerCase(),
      ),
    );
    if (group) setActiveTab(`group-${group.groupKey}` as ActiveTabId);
  }, [
    dualPreviewAccordion,
    isV2,
    navigationRequestToken,
    navigationTargetIndex,
    systems,
    v2View,
  ]);

  const footerAdjustmentsTotal = useMemo(
    () =>
      buildDtoolsReviewProposalTotals({
        systems,
        footerAdjustments: footer.adjustments,
      }).adjustmentsTotal,
    [systems, footer.adjustments],
  );
  const showRetailPriceDisclaimer = proposalShowsRetailPriceDisclaimer(
    customerPricing,
    footerAdjustmentsTotal,
  );

  const activeIndependentSection = useMemo(() => {
    if (!v2View || !activeTab.startsWith("independent-")) return null;
    return v2View.independentSections.find((section) => `independent-${section.sectionKey}` === activeTab) ?? null;
  }, [activeTab, v2View]);

  const activeAlternativeGroup = useMemo(() => {
    if (!v2View || !activeTab.startsWith("group-")) return null;
    return v2View.alternativeGroups.find((group) => `group-${group.groupKey}` === activeTab) ?? null;
  }, [activeTab, v2View]);

  const webProposalBody = (
    <div className="dtools-proposal-screen-only space-y-6">
      <ProposalSectionTabNav
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={(id) => setActiveTab(id as ActiveTabId)}
        stickyFlushTop
        data-testid="dtools-customer-proposal-tabs"
      />

      {activeTab === "clarity" ? (
        <div className="dtools-v2-screen-content">
          <DtoolsClarityTabPanel customerIntroduction={presentation?.customerIntroduction} />
        </div>
      ) : null}

      {activeTab === "summary" && isV2 && v2View ? (
        <div className="dtools-v2-screen-content">
          <DtoolsV2SystemSummaryTab
            view={v2View}
            systems={systems}
            footer={footer}
            presentation={presentation}
            displaySettings={displaySettings}
            reviewContext={enrichedContext}
            audience={previewVisibilityAudience}
            customerPricing={customerPricing}
            optionActions={enrichedOptionActions}
          />
        </div>
      ) : null}

      {activeTab === "summary" && !isV2 ? (
        <div className="dtools-v2-screen-content">
          <DtoolsV1SystemSummaryTab
            systems={systems}
            footer={footer}
            presentation={presentation}
            displaySettings={displaySettings}
            reviewContext={enrichedContext}
            audience={previewVisibilityAudience}
            customerPricing={customerPricing}
            optionActions={enrichedOptionActions}
          />
        </div>
      ) : null}

      {isV2 && activeIndependentSection ? (
        <div
          id={optionSectionIds?.[
            systems.findIndex(
              (system) =>
                String(system.systemId ?? system.name).trim().toLowerCase() ===
                String(
                  activeIndependentSection.system.systemId ??
                    activeIndependentSection.system.name,
                )
                  .trim()
                  .toLowerCase(),
            )
          ]}
          className="dtools-v2-screen-content"
        >
          <DtoolsV2IndependentSectionView
            section={activeIndependentSection}
            footer={footer}
            reviewContext={enrichedContext}
            optionActions={enrichedOptionActions}
            customerPricing={customerPricing}
          />
        </div>
      ) : null}

      {isV2 && activeAlternativeGroup ? (
        <div
          id={navigationTargetIndex >= 0 ? optionSectionIds?.[navigationTargetIndex] : undefined}
          className="dtools-v2-screen-content"
        >
          <DtoolsV2AlternativeGroupView
            group={activeAlternativeGroup}
            footer={footer}
            reviewContext={enrichedContext}
            optionActions={enrichedOptionActions}
            customerPricing={customerPricing}
            navigationSystemKey={navigationSystemKey}
            navigationRequestToken={navigationRequestToken}
          />
        </div>
      ) : null}

      {!isV2 && activeTab.startsWith("option-")
        ? (() => {
            const optionIndex = Number(activeTab.replace("option-", ""));
            const system = systems[optionIndex];
            if (!system || isDtoolsOptionHidden(system, displaySettings)) return null;
            const customerSystem = applyCustomerFacingRoomVisibility(
              system,
              displaySettings,
              previewVisibilityAudience,
            );
            return (
              <section
                id={optionSectionIds?.[optionIndex]}
                className="dtools-v2-screen-content"
                data-testid={`dtools-customer-tab-option-${optionIndex}`}
              >
                <DtoolsReviewSystemSection
                  system={customerSystem}
                  reviewContext={enrichedContext}
                  optionIndex={optionIndex}
                  optionActions={enrichedOptionActions}
                  showRoomBreakdown={false}
                  showEquipmentSort
                  hideSectionTotal
                  customerSystemPage
                  showRetailPriceDisclaimer={showRetailPriceDisclaimer}
                  customerPricing={customerPricing}
                />
              </section>
            );
          })()
        : null}

      {activeTab === "terms" ? (
        <section className="space-y-6 dtools-v2-screen-content" data-testid="dtools-customer-tab-terms">
          {!hideCombinedProposalPricing && customerPricing?.showFinalAgreedPrice ? (
            <ProposalCustomerPricingPanelFromDisplay pricing={customerPricing} />
          ) : null}
          <ProposalRetailTermsCollapsible
            paymentTerms={paymentTerms}
            createdAt={createdAt}
            linkBExpiresAt={linkBExpiresAt}
            defaultExpanded
          />
          {presentation ? <DtoolsProposalDisclaimersFooter presentation={presentation} /> : null}
        </section>
      ) : null}
    </div>
  );

  const pdfProposalBody = cover ? (
    <DtoolsProposalPdfPackage
      systems={systems}
      footer={footer}
      reviewContext={enrichedContext}
      presentation={presentation}
      paymentTerms={paymentTerms}
      displaySettings={displaySettings}
      tabModelVersion={tabModelVersion}
      systemClassifications={systemClassifications}
      parsedMetadataJson={parsedMetadataJson}
      customerPricing={customerPricing}
      retailPricingJson={retailPricingJson}
      totalAmount={totalAmount}
      cover={cover}
      pdfPackageMeta={pdfPackageMeta}
      createdAt={createdAt}
      linkBExpiresAt={linkBExpiresAt}
      approvedAt={approvedAt}
      status={status}
      pipelineStage={pipelineStage}
    />
  ) : null;

  return (
    <div
      className={isV2 ? "dtools-v2-customer-preview space-y-6" : "space-y-6"}
      style={{ fontFamily: PROPOSAL_COPY_FONT }}
      data-testid="dtools-customer-tabbed-preview"
    >
      {dualPreviewAccordion && cover ? (
        <div className="space-y-3" data-testid="dtools-dual-preview-accordion">
          <DtoolsReviewAccordionSection
            id="dtools-preview-web"
            title="Web Proposal"
            description="Interactive proposal tabs as customers see them on screen."
            testId="dtools-preview-web-accordion"
            open={previewSurface === "web"}
            onOpenChange={(open) => {
              if (open) setPreviewSurface("web");
            }}
          >
            {webProposalBody}
          </DtoolsReviewAccordionSection>
          <DtoolsReviewAccordionSection
            id="dtools-preview-pdf"
            title="PDF Proposal"
            description="Printable quotation package layout."
            testId="dtools-preview-pdf-accordion"
            open={previewSurface === "pdf"}
            onOpenChange={(open) => {
              if (open) setPreviewSurface("pdf");
            }}
          >
            {pdfProposalBody}
          </DtoolsReviewAccordionSection>
        </div>
      ) : (
        <>
          {webProposalBody}
          {pdfProposalBody}
        </>
      )}
    </div>
  );
}
